import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Eye, UserPlus, Filter, Star, Tag, Search, Trophy, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FreelanceModels = () => {
  const [models, setModels] = useState([]);
  const [filteredModels, setFilteredModels] = useState([]);
  const [featuredModels, setFeaturedModels] = useState([]);
  const [modelOfTheWeek, setModelOfTheWeek] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    let filtered = models;
    
    if (selectedCategory) {
      filtered = filtered.filter(m => m.categories?.includes(selectedCategory));
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(m => m.name.toLowerCase().includes(query));
    }
    
    setFilteredModels(filtered);
  }, [selectedCategory, searchQuery, models]);

  const fetchData = async () => {
    try {
      const [modelsRes, categoriesRes, featuredRes, motwRes] = await Promise.all([
        axios.get(`${API}/models`),
        axios.get(`${API}/models/categories`),
        axios.get(`${API}/models/featured/list`),
        axios.get(`${API}/models/model-of-the-week`)
      ]);
      setModels(modelsRes.data);
      setFilteredModels(modelsRes.data);
      setCategories(categoriesRes.data.categories || []);
      setFeaturedModels(featuredRes.data || []);
      if (motwRes.data.model) {
        setModelOfTheWeek({ ...motwRes.data.model, weeklyViews: motwRes.data.weekly_views });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSelectedCategory('');
    setSearchQuery('');
  };

  const ModelCard = ({ model, isFeatured = false, isModelOfWeek = false }) => (
    <Link 
      to={`/models/${model.id}`}
      className={`group bg-zinc-900 rounded-lg overflow-hidden border transition-all duration-300 relative ${
        isModelOfWeek
          ? 'border-amber-500/50 hover:border-amber-400'
          : isFeatured 
            ? 'border-yellow-500/50 hover:border-yellow-400' 
            : 'border-red-900/20 hover:border-red-500/50'
      }`}
      data-testid={`model-card-${model.id}`}
    >
      {/* Model of the Week Badge */}
      {isModelOfWeek && (
        <div className="absolute top-3 left-3 z-10 flex items-center gap-1 bg-gradient-to-r from-amber-500 to-orange-500 text-black px-3 py-1 rounded-full text-xs font-bold shadow-lg">
          <Trophy size={12} fill="currentColor" /> Model of the Week
        </div>
      )}
      
      {/* Featured Badge */}
      {isFeatured && !isModelOfWeek && (
        <div className="absolute top-3 left-3 z-10 flex items-center gap-1 bg-yellow-500 text-black px-3 py-1 rounded-full text-xs font-bold">
          <Star size={12} fill="currentColor" /> Featured
        </div>
      )}
      
      {/* Main Image */}
      <div className="aspect-[3/4] overflow-hidden relative">
        <img 
          src={model.image_urls[0] ? `${BACKEND_URL}${model.image_urls[0]}` : 'https://via.placeholder.com/400x500?text=No+Image'}
          alt={model.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      
      {/* Info */}
      <div className="p-6">
        <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-red-400 transition-colors">
          {model.name}
        </h3>
        
        {/* Categories */}
        {model.categories && model.categories.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {model.categories.slice(0, 3).map(cat => (
              <span key={cat} className="text-xs bg-zinc-800 text-gray-400 px-2 py-1 rounded">
                {cat}
              </span>
            ))}
            {model.categories.length > 3 && (
              <span className="text-xs text-gray-500">+{model.categories.length - 3}</span>
            )}
          </div>
        )}
        
        {model.bio && (
          <p className="text-gray-400 text-sm mb-4 line-clamp-2">
            {model.bio}
          </p>
        )}
        
        <div className="flex items-center gap-4 text-gray-500 text-sm">
          {model.instagram_url && (
            <span className="flex items-center gap-1">
              <Instagram size={14} />
              Instagram
            </span>
          )}
          <span className="flex items-center gap-1">
            <Eye size={14} />
            {model.profile_views || 0} views
          </span>
        </div>
      </div>
    </Link>
  );

  return (
    <div className="bg-black text-white min-h-screen pt-32 pb-20">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="max-w-4xl mx-auto text-center mb-12">
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-red-500 mb-6 uppercase">
            Freelance Models
          </h1>
          <p className="text-lg text-gray-300 leading-relaxed mb-8">
            Browse our curated directory of professional freelance models available for 
            fashion shoots, editorial work, boudoir photography, and creative productions.
          </p>
          <Link 
            to="/model-signup"
            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-full font-mono text-sm tracking-widest uppercase transition-all"
            data-testid="become-model-btn"
          >
            <UserPlus size={18} /> Become a Model
          </Link>
        </div>

        {/* Model of the Week Section */}
        {modelOfTheWeek && (
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <Trophy className="text-amber-500" size={24} fill="currentColor" />
              <h2 className="text-2xl font-semibold text-white">Model of the Week</h2>
              <span className="text-amber-400 text-sm">({modelOfTheWeek.weeklyViews} views this week)</span>
            </div>
            <div className="max-w-sm">
              <ModelCard model={modelOfTheWeek} isModelOfWeek={true} />
            </div>
          </div>
        )}

        {/* Featured Models Section */}
        {featuredModels.length > 0 && (
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <Star className="text-yellow-500" size={24} fill="currentColor" />
              <h2 className="text-2xl font-semibold text-white">Featured Models</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featuredModels.map((model) => (
                <div key={model.id} className="relative">
                  <ModelCard model={model} isFeatured={true} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search and Filter Section */}
        <div className="mb-8 space-y-6">
          {/* Search Bar */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Search className="text-red-500" size={20} />
              <h3 className="text-lg font-semibold text-white">Search Models</h3>
            </div>
            <div className="relative max-w-md">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name..."
                className="w-full bg-zinc-900 border border-red-900/30 rounded-full px-5 py-3 pl-12 text-white placeholder-gray-500 focus:outline-none focus:border-red-500 transition-colors"
                data-testid="model-search-input"
              />
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                >
                  <X size={18} />
                </button>
              )}
            </div>
          </div>

          {/* Category Filter */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Filter className="text-red-500" size={20} />
              <h3 className="text-lg font-semibold text-white">Filter by Category</h3>
              {(selectedCategory || searchQuery) && (
                <button
                  onClick={clearFilters}
                  className="text-red-400 text-sm hover:text-red-300 ml-auto"
                >
                  Clear all filters
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2" data-testid="category-filter">
              <button
                onClick={() => setSelectedCategory('')}
                className={`px-4 py-2 rounded-full text-sm transition-all ${
                  selectedCategory === ''
                    ? 'bg-red-600 text-white'
                    : 'bg-zinc-800 text-gray-400 hover:bg-zinc-700 hover:text-white'
                }`}
              >
                All Models
              </button>
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm transition-all flex items-center gap-1 ${
                    selectedCategory === category
                      ? 'bg-red-600 text-white'
                      : 'bg-zinc-800 text-gray-400 hover:bg-zinc-700 hover:text-white'
                  }`}
                >
                  <Tag size={14} /> {category}
                </button>
              ))}
            </div>
            {selectedCategory && (
              <p className="text-gray-500 text-sm mt-2">
                Showing {filteredModels.length} model{filteredModels.length !== 1 ? 's' : ''} in "{selectedCategory}"
              </p>
            )}
          </div>
        </div>

        {/* Models Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredModels.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-400 text-lg mb-6">
              {selectedCategory 
                ? `No models found in "${selectedCategory}" category.`
                : 'No models available at the moment.'
              }
            </p>
            {selectedCategory ? (
              <button
                onClick={() => setSelectedCategory('')}
                className="text-red-400 hover:text-red-300 transition-colors"
              >
                Clear filter
              </button>
            ) : (
              <Link 
                to="/model-signup"
                className="inline-flex items-center gap-2 text-red-400 hover:text-red-300 transition-colors"
              >
                <UserPlus size={18} /> Be the first to join our directory
              </Link>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="models-grid">
            {filteredModels.map((model) => (
              <ModelCard key={model.id} model={model} />
            ))}
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-20">
          <h2 className="text-3xl font-semibold text-white mb-8 text-center">Join Our Directory</h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/model-signup"
              className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-full font-mono text-sm tracking-widest uppercase transition-all"
              data-testid="join-model-cta"
            >
              Join as Model →
            </Link>
            <Link 
              to="/join-our-team"
              className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-red-600 hover:bg-red-600/10 text-red-500 px-8 py-4 rounded-full font-mono text-sm tracking-widest uppercase transition-all"
              data-testid="join-photographer-cta"
            >
              Join as Photographer
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FreelanceModels;
